import React, { useEffect, useMemo, useState } from 'react';
import ChatWidgetContainer from './ChatWidget/ChatWidgetContainer';
import { captureLead } from '../api/services/widgetService';
import './visitor-chat-widget.css';

const TASKS = [
  ['launcher', 'Launcher & open/close'],
  ['greeting', 'Initial greeting'],
  ['chips', 'Suggested questions'],
  ['composer', 'Visitor message composer'],
  ['bubble', 'AI response bubble'],
  ['session', 'Active-session context'],
  ['typing', 'Typing / loading'],
  ['error', 'API / AI error'],
  ['history', 'Conversation history'],
  ['lead-name', 'Lead capture name'],
  ['lead-email', 'Lead capture email'],
];

const INITIAL_HISTORY = [
  { id: 1, from: 'AI', text: 'Hi! Welcome to ChatBiz AI 👋 How can I help you today?', time: '10:23 AM' },
  { id: 2, from: 'Visitor', text: 'I want to know about your pricing.', time: '10:24 AM' },
  { id: 3, from: 'AI', text: 'Sure — I can help with plans and pricing.', time: '10:24 AM' },
];

export default function VisitorChatWidgetPage() {
  const [active, setActive] = useState('launcher');
  const [typing, setTyping] = useState(false);
  const [apiState, setApiState] = useState('idle');
  const [history, setHistory] = useState(INITIAL_HISTORY);
  // The widget owns the session; this is the display copy it publishes (localStorage + event).
  const [sessionContext, setSessionContext] = useState(() => {
    const idle = { sessionId: 'Not started yet', channel: 'Website', status: 'Idle', visitorName: '', leadName: '', leadEmail: '' };
    try { return JSON.parse(window.localStorage.getItem('chatbizz_active_session')) || idle; } catch { return idle; }
  });
  const [leadName, setLeadName] = useState(() => sessionContext.leadName || sessionContext.visitorName || '');
  const [leadEmail, setLeadEmail] = useState(() => sessionContext.leadEmail || '');
  const [leadMessage, setLeadMessage] = useState('');

  useEffect(() => {
    const onSessionUpdate = (event) => {
      if (event.detail) setSessionContext(event.detail);
    };
    window.addEventListener('chatbizz_session_updated', onSessionUpdate);
    return () => window.removeEventListener('chatbizz_session_updated', onSessionUpdate);
  }, []);

  const title = useMemo(() => TASKS.find(([id]) => id === active)?.[1] || '', [active]);

  const selectTask = (id) => {
    setActive(id);
    window.requestAnimationFrame(() => {
      const target = document.getElementById(`visitor-task-${id}`);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const simulateTyping = () => {
    setTyping(true);
    window.setTimeout(() => setTyping(false), 1400);
  };

  const simulateApi = (state) => {
    setApiState('loading');
    window.setTimeout(() => setApiState(state), 700);
  };

  const saveLead = async (e) => {
    e.preventDefault();
    const cleanName = leadName.trim();
    const cleanEmail = leadEmail.trim().toLowerCase();
    if (!cleanName || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setLeadMessage('Please enter a valid name and email.');
      return;
    }
    // A lead belongs to a real conversation: the visitor must have started a chat in the widget.
    if (!String(sessionContext.sessionId).startsWith('wc_')) {
      setLeadMessage('Start a chat in the widget first (send a message), then save your details.');
      return;
    }
    try {
      const result = await captureLead(sessionContext.sessionId, { name: cleanName, email: cleanEmail });
      setLeadMessage(result.duplicate ? 'Lead details saved successfully (existing lead matched by email).' : 'Lead details saved successfully.');
      window.dispatchEvent(new CustomEvent('chatbizz_lead_captured', { detail: { name: cleanName, email: cleanEmail, source: 'Website' } }));
    } catch (err) {
      setLeadMessage(err.details?.fields?.email || err.details?.fields?.name || err.message || 'Could not save the lead.');
    }
  };

  return (
    <div className="vc-page">
      <div className="vc-heading">
        <div>
          <span className="vc-eyebrow">WORKSPACE / VISITOR CHAT</span>
          <h1>Visitor Chat Widget</h1>
          <p>Configure and verify the customer-facing chat experience inside the Authentication Dashboard.</p>
        </div>
        <span className="vc-integrated"><i /> Integrated</span>
      </div>

      <div className="vc-task-strip" role="tablist" aria-label="Visitor Chat Widget tasks">
        {TASKS.map(([id, label]) => (
          <button key={id} type="button" className={active === id ? 'active' : ''} onClick={() => selectTask(id)}>
            {label}
          </button>
        ))}
      </div>

      <div className="vc-current">Current task <strong>{title}</strong></div>

      <div className="vc-layout">
        <section id={`visitor-task-${active}`} className="vc-card" aria-live="polite">
          {active === 'launcher' && <>
            <h2>Launcher and open / close behavior</h2>
            <p>The live widget uses a closed → open → notified state model with keyboard and outside-click dismissal.</p>
            <div className="vc-state-grid">
              <StateCard title="Closed" text="Floating launcher is visible; panel is hidden." />
              <StateCard title="Open" text="Panel is visible and the composer receives focus." />
              <StateCard title="Notified" text="Unread badge appears when a new inbound message arrives while closed." />
            </div>
            <Checklist items={['Fixed bottom-right launcher', 'Open / close toggle', 'Escape and outside-click close', 'Unread badge and accessible labels', 'Reduced-motion support']} />
          </>}

          {active === 'greeting' && <>
            <h2>Initial greeting UI</h2>
            <p>The widget opens with a customer-facing greeting and online assistant state.</p>
            <div className="vc-sample-bubble">Hi! Welcome to ChatBiz AI 👋<br />How can I help you today?</div>
            <Checklist items={['Bot name and avatar', 'Online status', 'Greeting message', 'Responsive presentation']} />
          </>}

          {active === 'chips' && <>
            <h2>Suggested question chips</h2>
            <p>Quick replies are keyboard-operable and add the selected request to the conversation.</p>
            <div className="vc-chip-row"><button>Pricing Plans</button><button>Book a Demo</button><button>Technical Support</button></div>
          </>}

          {active === 'composer' && <>
            <h2>Visitor message composer</h2>
            <p>The live preview at the bottom-right contains the real text input and send action. Empty messages are blocked.</p>
            <Checklist items={['Text input', 'Whitespace validation', 'Disabled empty send button', 'Enter-to-send behavior', 'Independent message scrolling']} />
          </>}

          {active === 'bubble' && <>
            <h2>AI response message bubble</h2>
            <p>AI and visitor messages use distinct bubbles with timestamps for readable conversation history.</p>
            <div className="vc-bubble-row"><span className="vc-ai-bubble">I can help with that.</span><span className="vc-user-bubble">Thanks!</span></div>
          </>}

          {active === 'session' && <>
            <h2>Active-session context</h2>
            <div className="vc-session-grid"><Info label="Session" value={sessionContext.sessionId} /><Info label="Channel" value={sessionContext.channel} /><Info label="Status" value={sessionContext.status} /><Info label="Agent" value={sessionContext.assignedAgent || "Not assigned"} /><Info label="Visitor name" value={sessionContext.visitorName || 'Not captured yet'} /><Info label="Lead email" value={sessionContext.leadEmail || 'Not captured yet'} /></div>
            <Checklist items={['Session identifier', 'Channel context', 'Active status', 'Visitor name is remembered within the session', 'Context remains visible without changing dashboard shell']} />
          </>}

          {active === 'typing' && <>
            <h2>Typing / loading state</h2>
            <p>Verify a non-blocking loading state before an AI response is returned.</p>
            <button className="vc-primary" onClick={simulateTyping}>{typing ? 'AI is typing…' : 'Simulate AI typing'}</button>
            {typing && <div className="vc-loading"><span /><span /><span /> Assistant is typing…</div>}
          </>}

          {active === 'error' && <>
            <h2>Graceful API / AI error state</h2>
            <p>Loading, success and error states are explicit so failures do not leave the composer stuck.</p>
            <div className="vc-actions"><button className="vc-primary" onClick={() => simulateApi('success')}>Simulate success</button><button className="vc-danger" onClick={() => simulateApi('error')}>Simulate error</button></div>
            {apiState === 'loading' && <div className="vc-state loading">Loading response…</div>}
            {apiState === 'success' && <div className="vc-state success">Response delivered successfully.</div>}
            {apiState === 'error' && <div className="vc-state error">We could not reach the AI service. Please retry.</div>}
          </>}

          {active === 'history' && <>
            <h2>Conversation history presentation</h2>
            <div className="vc-history">{(sessionContext.history && sessionContext.history.length ? sessionContext.history : history).map(m => <div key={m.id} className="vc-history-row"><b>{m.from}</b><span>{m.text}</span><small>{m.time}</small></div>)}</div>
            <button className="vc-secondary" onClick={() => setHistory(h => [...h, { id: Date.now(), from: 'AI', text: 'History entry added.', time: 'Now' }])}>Add history entry</button>
          </>}

          {active === 'lead-name' && <>
            <h2>Lead capture — name</h2>
            <p>Collect a visitor name with validation and save it to the active visitor session.</p>
            <Field label="Visitor name" value={leadName} onChange={setLeadName} placeholder="Enter your name" />
            <button type="button" className="vc-primary" onClick={(e) => {
              e.preventDefault();
              const name = leadName.trim();
              if (!name) { setLeadMessage('Name is required.'); return; }
              const next = { ...sessionContext, visitorName: name, leadName: name };
              setSessionContext(next);
              setLeadMessage('Name saved successfully.');
              window.dispatchEvent(new CustomEvent('chatbizz_session_updated', { detail: { ...next, source: 'page' } }));
            }}>Save name</button>
            {leadMessage && <div className={`vc-state ${leadMessage.includes('successfully') ? 'success' : 'error'}`}>{leadMessage}</div>}
          </>}

          {active === 'lead-email' && <>
            <h2>Lead capture — email</h2>
            <p>Collect and validate an email address as part of the visitor conversation flow.</p>
            <form onSubmit={saveLead}>
              <Field label="Name" value={leadName} onChange={setLeadName} placeholder="Enter your name" />
              <Field label="Email" value={leadEmail} onChange={setLeadEmail} placeholder="name@example.com" type="email" />
              <button className="vc-primary" type="submit">Save lead details</button>
            </form>
            {leadMessage && <div className={`vc-state ${leadMessage.includes('successfully') ? 'success' : 'error'}`}>{leadMessage}</div>}
          </>}
        </section>

        <aside className="vc-side">
          <div className="vc-card vc-preview-card">
            <div className="vc-preview-head"><div><b>Live customer preview</b><small>Real interactive visitor widget</small></div><span>Live</span></div>
            <div className="vc-preview-copy"><h3>Smarter Support</h3><strong>Happier Customers!</strong><p>Use the floating launcher to open the visitor chat. Try quick replies, typing and message sending.</p></div>
          </div>
          <div className="vc-card">
            <h3>Acceptance checklist</h3>
            <Checklist items={['Responsive down to 320px', 'Poppins typography', 'WCAG keyboard operation', 'ARIA dialog and launcher labels', 'No host-page layout shift']} />
          </div>
        </aside>
      </div>

      <ChatWidgetContainer botName="ChatBiz AI" />
    </div>
  );
}

function Checklist({ items }) { return <div className="vc-checklist">{items.map(x => <div key={x}><i>✓</i>{x}</div>)}</div>; }
function StateCard({ title, text }) { return <div className="vc-state-card"><b>{title}</b><span>{text}</span></div>; }
function Info({ label, value }) { return <div className="vc-info"><small>{label}</small><b>{value}</b></div>; }
function Field({ label, value, onChange, placeholder, type='text' }) { return <label className="vc-field"><span>{label}</span><input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} /></label>; }
